import { ISaveInstruction } from "global/Application/dataActions";
import { emitEventForBenefitPropertyChange, emitEventForFieldChange } from "global/EmitEvent/Delta";
import { formatDate, parseDate } from "global/Utility/TSFunctions";
import { bcbs_groupconfigurationsnapshot } from "model/CRM";
import { IAccountsReceivable, IBenefit, IFeesAndPremiums, IFlexibleField, IFlexibleFieldSet, IGroupConfigurationConfig, IGroupConfigurationData, IGroupNumber, INonStandard, IPharmacyORE, IPlan, IStopLoss } from "model/FlexibleAttributeModel";
import { BaseJsonTransformer, ITransformContext } from "./IJsonTransformer";

/**
 * Updates attribute sort order and adds in any missing attributes given by the release.
 * NOTE: If fields no longer exist (which shouldn't happen), the fields will be appended
 * to the end of the field array (ensuring no data loss).
 */
export class CloneTransformer extends BaseJsonTransformer {
    readonly release: IGroupConfigurationConfig;
    readonly snapshot: bcbs_groupconfigurationsnapshot;
    readonly emit: boolean;
    readonly instructions: ISaveInstruction | null;

    constructor(
        release: IGroupConfigurationConfig,
        snapshot: bcbs_groupconfigurationsnapshot,
        emit: boolean,
        instructions: ISaveInstruction | null
    ) {
        super();
        this.release = release;
        this.snapshot = snapshot;
        this.emit = emit;
        this.instructions = instructions;
    }

    async before(json: IGroupConfigurationData): Promise<void> {
    }

    async transformGroupConfiguration(json: IGroupConfigurationData): Promise<void> {
        this.processFields(json.fields);
    }

    async transformAttributeSet(attributeSet: IFlexibleFieldSet, context: ITransformContext): Promise<boolean | void> {
        this.processFields(attributeSet.fields);
    }

    async transformNonStandard(nonStandard: INonStandard, context: ITransformContext): Promise<boolean | void> {
        this.processFields(nonStandard.fields);
    }

    async transformBenefit(benefit: IBenefit): Promise<boolean | void> {
        emitEventForBenefitPropertyChange(this.snapshot, null, benefit, true);

        this.processFields(benefit.fields, benefit);
    }

    async transformAccountsReceivable(accountsReceivable: IAccountsReceivable): Promise<boolean | void> {
        this.processFields(accountsReceivable.fields);
    }

    async transformStopLoss(stopLoss: IStopLoss): Promise<boolean | void> {
        this.processFields(stopLoss.fields);
    }

    async transformFeesAndPremium(feesAndPremium: IFeesAndPremiums): Promise<boolean | void> {
        this.processFields(feesAndPremium.fields);
    }

    async transformPlan(plan: IPlan): Promise<boolean | void> {
        this.processFields(plan.fields);
    }

    async transformGroupNumber(groupNumber: IGroupNumber): Promise<boolean | void> {
        this.processFields(groupNumber.fields);
    }

    async after(json: IGroupConfigurationData): Promise<void> {
        // TODO not sure what this one is for and how it works, may also need to check how it interacts with group collect integration not just local cloning cases
        if (this.snapshot.bcbs_operativedate) {
            json.operativeDate = {
                type: 'date',
                value: formatDate(parseDate(this.snapshot.bcbs_operativedate, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:yyyy-MM-dd')
            };
        }
    }

    processFields(sourceFields: IFlexibleField[], benefit?: IBenefit): void {
        sourceFields.forEach(field => {

            const configAttribute = this.release.cache.map_bcbs_config_attribute[field.configRef.id];

            field.values.forEach(value => {

                // remove traces for old emit events
                delete value.emitRefId;

                if (this.emit)
                    if (value.value)
                        if (configAttribute?.bcbs_isemiteventonclone) {

                            const emitTracker = emitEventForFieldChange(
                                this.release,
                                this.snapshot,
                                benefit ?? null,
                                field,
                                value,
                                value.value,
                                true
                            );

                            if (emitTracker)
                                value.emitRefId = emitTracker?.bcbs_groupconfigurationsnapshoteventid;
                        }
            });
        });
    }
}
